import AnalyticsIcon from '@mui/icons-material/Analytics';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

import type { NavItem } from '@/components/layout/Sidebar';
import type { ErpModule } from '@/api/platform.api';
import { isSettingsLegacyRoute } from '@/config/settingsRegistry';
import { FEATURE } from '@/core/features/featureCodes';
import { moduleSharedPath } from '@/config/moduleSharedPaths';
import { lockedModulePath } from '@/config/moduleDefaults';

export type ModuleRelatedBundle = {
  routes: string[];
  apis: string[];
  permissionModules: string[];
  navItems: NavItem[];
};

const empty: ModuleRelatedBundle = { routes: [], apis: [], permissionModules: [], navItems: [] };

/** Supporting routes, APIs, and sidebar links per ERP module (beyond workspace defaults). */
export const MODULE_RELATED: Record<ErpModule, ModuleRelatedBundle> = {
  POS: {
    routes: [
      '/pos/dashboard',
      '/pos/stores',
      '/pos/products',
      '/pos/categories',
      '/pos/brands',
      '/pos/units',
      '/pos/tax-rules',
      '/pos/store-products',
      '/pos/invoices',
      '/pos/inventory',
      '/pos/inventory/ledger',
      '/warehouse',
      '/pos/product-alerts',
      '/pos/suppliers',
      '/procurement',
      '/vendor-portal',
      '/pos/discounts',
      '/pos/customers',
      '/pos/reports',
      lockedModulePath('inventory'),
      lockedModulePath('loyalty'),
      lockedModulePath('analytics'),
      lockedModulePath('procurement'),
    ],
    apis: [
      '/v1/stores',
      '/v1/products',
      '/v1/categories',
      '/v1/brands',
      '/v1/units',
      '/v1/tax-rules',
      '/v1/store-products',
      '/v1/customers',
      '/v1/inventory',
      '/v1/inventory/platform',
      '/v1/inventory/serials',
      '/v1/inventory/backorders',
      '/v1/discounts',
      '/v1/product-alerts',
      '/v1/billing-counters',
      '/v1/devices',
      '/v1/warehouses',
      '/v1/warehouse',
      '/v1/reports',
      '/v1/procurement',
      '/v1/suppliers',
      '/v1/shared/contracts',
      '/v1/shared/vendors',
      '/v1/vendor-portal',
    ],
    permissionModules: [
      'BILLING',
      'INVENTORY',
      'PRODUCT',
      'STORE',
      'WAREHOUSE',
      'PROCUREMENT',
      'CUSTOMER',
      'DISCOUNT',
    ],
    navItems: [],
  },
  INVENTORY: empty,
  CRM: {
    routes: [moduleSharedPath('CRM', 'customers'), lockedModulePath('crm')],
    apis: ['/v1/customers'],
    permissionModules: ['CRM', 'CUSTOMER'],
    navItems: [{ to: moduleSharedPath('CRM', 'customers'), label: 'Customers', icon: PeopleIcon }],
  },
  LOYALTY: empty,
  REPORTING: empty,
  PROCUREMENT: {
    routes: [
      moduleSharedPath('PROCUREMENT', 'suppliers'),
      moduleSharedPath('PROCUREMENT', 'inventory'),
      moduleSharedPath('PROCUREMENT', 'inventory/ledger'),
      moduleSharedPath('PROCUREMENT', 'products'),
      '/warehouse',
      lockedModulePath('procurement'),
    ],
    apis: [
      '/v1/suppliers',
      '/v1/inventory',
      '/v1/inventory/platform',
      '/v1/products',
      '/v1/warehouse',
      '/v1/warehouses',
      '/v1/vendor-portal',
      '/v1/shared/contracts',
      '/v1/shared/vendors',
    ],
    permissionModules: ['PROCUREMENT', 'INVENTORY', 'PRODUCT', 'WAREHOUSE'],
    navItems: [],
  },
  ANALYTICS: empty,
  PROJECTS: {
    routes: [moduleSharedPath('PROJECTS', 'customers'), lockedModulePath('projects')],
    apis: ['/v1/customers'],
    permissionModules: ['PROJECTS', 'CUSTOMER'],
    navItems: [{ to: moduleSharedPath('PROJECTS', 'customers'), label: 'Customers', icon: PeopleIcon }],
  },
  HR: {
    routes: [lockedModulePath('hr'), lockedModulePath('payroll')],
    apis: [],
    permissionModules: ['HR'],
    navItems: [],
  },
  FINANCE: {
    routes: ['/finance/invoices', '/procurement/vendor-bills', lockedModulePath('finance')],
    apis: ['/v1/billing', '/v1/procurement'],
    permissionModules: ['FINANCE', 'BILLING', 'PROCUREMENT'],
    navItems: [
      { to: '/finance/invoices', label: 'Sales invoices', icon: ReceiptIcon, feature: FEATURE.FINANCE_BASIC },
      {
        to: '/procurement/vendor-bills',
        label: 'Vendor bills',
        icon: ReceiptIcon,
        feature: FEATURE.PROCUREMENT_BASIC,
      },
    ],
  },
  PAYROLL: empty,
  DELIVERY: empty,
  ECOMMERCE: empty,
  WAREHOUSE: empty,
  SOCIAL: {
    routes: [lockedModulePath('social')],
    apis: [],
    permissionModules: ['SOCIAL'],
    navItems: [],
  },
  DESK: {
    routes: [moduleSharedPath('DESK', 'customers'), lockedModulePath('desk')],
    apis: ['/v1/customers'],
    permissionModules: ['DESK', 'CUSTOMER'],
    navItems: [{ to: moduleSharedPath('DESK', 'customers'), label: 'Customers', icon: PeopleIcon }],
  },
  LEAD_GEN: {
    routes: [moduleSharedPath('LEAD_GEN', 'customers'), lockedModulePath('crm')],
    apis: ['/v1/customers'],
    permissionModules: ['LEAD_GEN', 'CUSTOMER'],
    navItems: [{ to: moduleSharedPath('LEAD_GEN', 'customers'), label: 'Customers', icon: PeopleIcon }],
  },
  MARKETING: {
    routes: [moduleSharedPath('MARKETING', 'customers'), lockedModulePath('crm')],
    apis: ['/v1/customers'],
    permissionModules: ['MARKETING', 'CUSTOMER'],
    navItems: [{ to: moduleSharedPath('MARKETING', 'customers'), label: 'Customers', icon: PeopleIcon }],
  },
};

export function getModuleRelated(module: ErpModule): ModuleRelatedBundle {
  return MODULE_RELATED[module] ?? empty;
}

export function moduleRelatedNavItems(module: ErpModule): NavItem[] {
  return getModuleRelated(module).navItems.filter((item) => !isSettingsLegacyRoute(item.to));
}

/** POS operational nav (module-specific; masters live under Settings). */
export const POS_OPERATIONAL_NAV: NavItem[] = [
  { to: '/pos/reports', label: 'Reports', icon: AnalyticsIcon, feature: FEATURE.REPORTING },
  { to: '/pos/invoices', label: 'Invoices', icon: ReceiptIcon, feature: FEATURE.POS_BILLING },
  { to: '/pos/inventory', label: 'Inventory', icon: InventoryIcon, feature: FEATURE.INVENTORY },
  { to: '/pos/inventory/ledger', label: 'Stock ledger', icon: InventoryIcon, feature: FEATURE.INVENTORY },
  { to: '/pos/product-alerts', label: 'Product alerts', icon: NotificationsActiveIcon, feature: FEATURE.INVENTORY },
  { to: '/procurement', label: 'Procurement', icon: RequestQuoteIcon, feature: FEATURE.PROCUREMENT_BASIC },
  {
    to: '/procurement/purchase-requests',
    label: 'Purchase requests',
    icon: RequestQuoteIcon,
    feature: FEATURE.PROCUREMENT_BASIC,
  },
  {
    to: '/procurement/purchase-orders',
    label: 'Purchase orders',
    icon: ReceiptIcon,
    feature: FEATURE.PROCUREMENT_BASIC,
  },
  {
    to: '/procurement/goods-receipts',
    label: 'Goods receipts',
    icon: LocalShippingIcon,
    feature: FEATURE.PROCUREMENT_BASIC,
  },
  {
    to: '/procurement/approvals',
    label: 'Procurement approvals',
    icon: FactCheckIcon,
    feature: FEATURE.PROCUREMENT_APPROVALS,
  },
  { to: '/warehouse', label: 'Warehouse', icon: LocalShippingIcon, feature: FEATURE.WAREHOUSE },
  { to: '/warehouse/operations', label: 'WMS operations', icon: LocalShippingIcon, feature: FEATURE.WAREHOUSE },
];

/** Procurement module nav (primary routes + related catalog/stock). */
export const PROCUREMENT_OPERATIONAL_NAV: NavItem[] = [
  { to: '/procurement/purchase-requests', label: 'Purchase requests', icon: RequestQuoteIcon, feature: FEATURE.PROCUREMENT_BASIC },
  { to: '/procurement/rfqs', label: 'RFQs', icon: RequestQuoteIcon, feature: FEATURE.PROCUREMENT_RFQ },
  { to: '/procurement/purchase-orders', label: 'Purchase orders', icon: ReceiptIcon, feature: FEATURE.PROCUREMENT_BASIC },
  { to: '/procurement/goods-receipts', label: 'Goods receipts', icon: LocalShippingIcon, feature: FEATURE.PROCUREMENT_BASIC },
  { to: '/procurement/approvals', label: 'Approvals', icon: FactCheckIcon, feature: FEATURE.PROCUREMENT_APPROVALS },
  { to: '/procurement/vendor-bills', label: 'Vendor bills', icon: ReceiptIcon, feature: FEATURE.PROCUREMENT_BASIC },
  { to: '/procurement/reports', label: 'Reports', icon: AnalyticsIcon, feature: FEATURE.PROCUREMENT_ADVANCED_ANALYTICS },
];
